'use client';

import { Info } from 'lucide-react';

interface FacialRecognitionNoticeProps {
  /**
   * Where someone who wants to be removed should go. Passed in rather than
   * built from an album token, because the event's master link has no album:
   * one link now covers the whole race.
   */
  removalHref: string;
  selfRedactionEnabled: boolean;
}

/**
 * Transparency notice for everyone who appears in the event photos, not just the
 * person searching. Rekognition indexes every face in every uploaded photo, so
 * people in the background have biometric data processed too. GDPR requires we
 * tell them this is happening, why, and how to object or be removed. Always
 * visible on the public album page; links to the self-serve removal flow when
 * the organiser has enabled it.
 */
export function FacialRecognitionNotice({ removalHref, selfRedactionEnabled }: FacialRecognitionNoticeProps) {
  return (
    <div
      style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          padding: '14px 16px',
          borderRadius: '12px',
          border: '1px solid var(--fv-border)',
          background: 'var(--fv-bg-input)',
        }}
      >
        <Info size={16} style={{ color: 'var(--fv-text-secondary)', flex: '0 0 auto', marginTop: '2px' }} />
        <div style={{ fontSize: '12.5px', lineHeight: 1.6, color: 'var(--fv-text-secondary)', textAlign: 'left' }}>
          Photos from this event are processed with facial recognition so people can find their
          own pictures. Face data is used only to match photos and is deleted once the photos are
          no longer available (and within 12 months at the latest).{' '}
          {selfRedactionEnabled ? (
            <>
              If you appear in a photo and want it removed or your face blurred,{' '}
              <a
                href={removalHref}
                style={{ color: 'var(--fv-text-primary)', textDecoration: 'underline' }}
              >
                remove yourself here
              </a>
              .{' '}
            </>
          ) : (
            <>To have a photo removed or your face blurred, contact the event organiser.{' '}</>
          )}
          See our{' '}
          <a href="/privacy" style={{ color: 'var(--fv-text-primary)', textDecoration: 'underline' }}>
            privacy notice
          </a>{' '}
          for more.
        </div>
      </div>
    </div>
  );
}
